
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
*/
import React, { useMemo, useState, useRef } from 'react';
import { motion, cubicBezier, AnimatePresence } from 'framer-motion';
import { SparklesIcon, DiamondIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon } from '../components/icons';
import { useTranslation } from '../i18n/useTranslation';
import { campaigns } from '@shared/data/portfolioData';
import { portfolioService } from '../services/portfolioService';
import { membershipTiers } from '../constants';
import { AppPage, LifeSphere } from '../types';
import { PortfolioItem } from '@shared/types';
import { CDN_URLS, UIX_CONFIG } from '../config';
import { getImageUrl } from '../utils/imageUtils';
import LifeSphereSelector from '../components/LifeSphereSelector';
import Footer from '../components/Footer';
import { useSEO } from '../hooks/useSEO';
import SchemaMarkup from '../components/SchemaMarkup';
import StickyCTA from '../components/StickyCTA';
import PriceDisplay from '../components/PriceDisplay';
import { DiscountService } from '../services/discountService';

interface ServicesPageProps {
  onNavigate: (page: AppPage, params?: Record<string, string>) => void;
  onOpenVoucherModal: () => void;
}

const ServicesPage: React.FC<ServicesPageProps> = ({ onNavigate, onOpenVoucherModal }) => {
  const { t } = useTranslation();
  const [portfolioItems, setPortfolioItems] = useState<PortfolioItem[]>([]);

  React.useEffect(() => {
    let mounted = true;
    portfolioService.getActiveItems().then(items => {
        if (mounted) setPortfolioItems(items);
    });
    return () => { mounted = false; };
  }, []);

  useSEO(
    t('seo_services_title'),
    t('seo_services_description'),
    [t('seo_services_kw1'), t('seo_services_kw2'), t('seo_services_kw3'), t('seo_services_kw4')]
  );

  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [activeSphere, setActiveSphere] = useState<LifeSphere | undefined>(undefined);
  const [billingCycle, setBillingCycle] = useState<'weekly' | 'monthly' | 'quarterly'>('monthly');
  const [isCampaignsCollapsed, setIsCampaignsCollapsed] = useState(false);
  const [isServicesCollapsed, setIsServicesCollapsed] = useState(false);
  const [isMembershipsCollapsed, setIsMembershipsCollapsed] = useState(false);

  // Carousel Ref
  const campaignScrollRef = useRef<HTMLDivElement>(null);

  const categories = useMemo(() => {
    const allCategories = portfolioItems.map((item: any) => item.categoryKey);
    return [...new Set(allCategories)];
  }, [portfolioItems]);

  const filteredItems = useMemo(() => {
    let items = portfolioItems;
    if (activeSphere) {
      if (activeSphere === LifeSphere.EXECUTIVE_ASCENDANCE) {
        items = items.filter((item: any) => ['portfolio_category_specialty', 'portfolio_category_events'].includes(item.categoryKey));
      } else if (activeSphere === LifeSphere.ROMANTIC_RENAISSANCE) {
        items = items.filter((item: any) => ['portfolio_category_personal', 'portfolio_category_events'].includes(item.categoryKey));
      } else if (activeSphere === LifeSphere.EFFORTLESS_MATRIARCH) {
        items = items.filter((item: any) => ['portfolio_category_personal', 'portfolio_category_wardrobe'].includes(item.categoryKey));
      } else if (activeSphere === LifeSphere.CREATIVE_FLOURISHING) {
        items = items.filter((item: any) => ['portfolio_category_specialty', 'portfolio_category_wardrobe'].includes(item.categoryKey));
      }
    }
    if (activeCategory) {
      items = items.filter((item: any) => item.categoryKey === activeCategory);
    }
    return items;
  }, [activeCategory, activeSphere, portfolioItems]);

  const sectionVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: cubicBezier(0.42, 0, 0.58, 1), staggerChildren: 0.2 } },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const billingCycleButtonClass = "px-4 py-2 text-sm font-semibold rounded-full transition-colors";
  const activeBillingCycleButtonClass = "bg-indigo-600 text-white";
  const inactiveBillingCycleButtonClass = "bg-white text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600";


  const scrollCampaigns = (direction: 'left' | 'right') => {
    if (campaignScrollRef.current) {
      const { current } = campaignScrollRef;
      const scrollAmount = current.clientWidth * 0.8;
      current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

  const [showSticky, setShowSticky] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    const handleScroll = () => {
      if (containerRef.current) {
        setShowSticky(containerRef.current.scrollTop > 400);
      }
    };
    const currentContainer = containerRef.current;
    currentContainer?.addEventListener('scroll', handleScroll);
    return () => currentContainer?.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={sectionVariants}
      ref={containerRef}
      className="h-full flex flex-col items-center p-4 sm:p-8 bg-gray-50 dark:bg-[#18181B] text-gray-800 dark:text-[#F1F1F1] overflow-y-auto custom-scrollbar"
    >
      <div className="max-w-7xl w-full mx-auto">
        <motion.div variants={itemVariants} className="flex flex-col items-center mb-10">
          <DiamondIcon className="w-12 h-12 text-gray-700 dark:text-[#C0A172] mb-4" />
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] text-center leading-tight">
            {t('portfolioPage_title')}
          </h1>
          <p className="text-lg md:text-xl text-gray-600 dark:text-[#F1F1F1]/70 mt-3 text-center max-w-2xl">
            {t('portfolioPage_tagline')}
          </p>
        </motion.div>

        {/* Campaigns Section (Horizontal Carousel) */}
        <motion.section variants={sectionVariants} className="mb-16 relative group/carousel">
          <div className="flex items-center justify-between mb-8 border-b border-gray-100 dark:border-white/5 pb-4">
            <div className="text-left">
              <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-[#C0A172]">{t('portfolioPage_campaigns_title')}</h2>
              <p className="text-gray-600 dark:text-[#F1F1F1]/60 mt-2">{t('portfolioPage_campaigns_tagline')}</p>
            </div>
            <button
              onClick={() => setIsCampaignsCollapsed(!isCampaignsCollapsed)}
              className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              aria-label={isCampaignsCollapsed ? "Expand campaigns" : "Collapse campaigns"}
            >
              <ChevronDownIcon className={`w-6 h-6 transition-transform duration-300 ${isCampaignsCollapsed ? '-rotate-90' : 'rotate-0'}`} />
            </button>
          </div>

          <AnimatePresence initial={false}>
            {!isCampaignsCollapsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <div className="relative">
                  {/* Left Button */}
                  <button
                    onClick={() => scrollCampaigns('left')}
                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity disabled:opacity-0 -ml-4"
                    aria-label="Previous campaign"
                  >
                    <ChevronLeftIcon className="w-6 h-6" />
                  </button>

                  <div
                    ref={campaignScrollRef}
                    className="flex overflow-x-auto snap-x snap-mandatory gap-6 pb-6 scrollbar-hide px-2"
                  >
                    {campaigns.map((campaign: any) => (
                      <motion.div
                        key={campaign.id}
                        variants={itemVariants}
                        className="relative rounded-xl overflow-hidden shadow-lg group flex-shrink-0 snap-center w-[85vw] md:w-[600px]"
                        whileHover={{ y: -5 }}
                      >
                        <img src={getImageUrl(campaign.imageUrl)} alt={t(campaign.nameKey)} className="w-full h-80 object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent p-6 flex flex-col justify-end">
                          {campaign.badgeKey && (
                            <span className="absolute top-4 right-4 bg-amber-400 text-black text-[10px] font-black uppercase px-3 py-1.5 rounded-full shadow-md z-10">{t(campaign.badgeKey)}</span>
                          )}
                          <div className="mb-4">
                            <h3 className="text-2xl font-serif font-bold text-white drop-shadow-md leading-tight">{t(campaign.nameKey)}</h3>
                            <p className="text-gray-200 mt-1 text-sm drop-shadow-sm line-clamp-2">{t(campaign.descriptionKey)}</p>
                          </div>

                          <div className="flex flex-col gap-4">
                            <div className="flex flex-wrap gap-1.5 opacity-80">
                              {campaign.serviceIds.slice(0, 3).map((id: string) => {
                                const service = portfolioItems.find((s: any) => s.id === id);
                                return service ? <span key={id} className="text-[10px] font-bold bg-white/10 text-white px-2 py-1 rounded-full backdrop-blur-sm border border-white/5">{t(service.titleKey)}</span> : null;
                              })}
                            </div>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onNavigate('booking');
                              }}
                              className="w-full bg-white text-black hover:bg-gray-100 font-bold py-3 px-4 rounded-xl transition-all duration-300 transform active:scale-95 text-sm shadow-xl flex items-center justify-center gap-2 group/btn"
                            >
                              {t('portfolioPage_bookService')}
                              <SparklesIcon className="w-4 h-4 group-hover/btn:rotate-12 transition-transform" />
                            </button>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Right Button */}
                  <button
                    onClick={() => scrollCampaigns('right')}
                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 p-2 bg-white/80 dark:bg-gray-800/80 rounded-full shadow-lg opacity-0 group-hover/carousel:opacity-100 transition-opacity disabled:opacity-0 -mr-4"
                    aria-label="Next campaign"
                  >
                    <ChevronRightIcon className="w-6 h-6" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>

        {/* Voucher Section */}
        <motion.section variants={sectionVariants} className="mb-16 bg-gray-50 dark:bg-[#2A2A2A] rounded-3xl p-12 text-center border border-gray-100 dark:border-[#C0A172]/10 shadow-sm">
          <motion.div variants={itemVariants}>
            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1]">{t('voucher_title')}</h2>
            <p className="text-gray-600 dark:text-[#F1F1F1]/60 mt-2 max-w-2xl mx-auto">{t('voucher_tagline')}</p>
            <button onClick={onOpenVoucherModal} className="mt-10 bg-[#C0A172] hover:bg-[#A68A5E] text-white font-bold py-4 px-10 rounded-full transition-all duration-300 transform active:scale-95 text-lg shadow-xl">
              {t('voucher_cta')}
            </button>
          </motion.div>
        </motion.section>

        {/* Services Section */}
        <motion.section variants={sectionVariants} className="mb-16">
          <div className="flex items-center justify-between mb-8 border-b border-gray-100 dark:border-white/5 pb-4">
            <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-[#C0A172]">{t('portfolioPage_services_title')}</h2>
            <button
              onClick={() => setIsServicesCollapsed(!isServicesCollapsed)}
              className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              aria-label={isServicesCollapsed ? "Expand services" : "Collapse services"}
            >
              <ChevronDownIcon className={`w-6 h-6 transition-transform duration-300 ${isServicesCollapsed ? '-rotate-90' : 'rotate-0'}`} />
            </button>
          </div>

          <AnimatePresence initial={false}>
            {!isServicesCollapsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                {/* Life Sphere Selector */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="mb-12 border-b border-gray-100 dark:border-white/5 pb-8">
                  <LifeSphereSelector
                    activeSphere={activeSphere}
                    onSelect={(sphere) => setActiveSphere(sphere === activeSphere ? undefined : sphere)}
                  />
                </motion.div>

                {/* Category Filters */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="flex flex-wrap justify-center gap-2 mb-10">
                  <button
                    onClick={() => setActiveCategory(null)}
                    className={`px-6 py-2.5 text-sm font-bold rounded-full transition-all shadow-sm ${!activeCategory ? 'bg-[#C0A172] text-white shadow-[#C0A172]/20' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-[#2A2A2A] dark:text-[#F1F1F1] dark:hover:bg-[#3A3A3A] border border-gray-100 dark:border-[#C0A172]/20'}`}
                  >
                    {t('portfolioPage_all_categories')}
                  </button>
                  {categories.map((category: any) => (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={`px-6 py-2.5 text-sm font-bold rounded-full transition-all shadow-sm ${activeCategory === category ? 'bg-[#C0A172] text-white shadow-[#C0A172]/20' : 'bg-white text-gray-700 hover:bg-gray-50 dark:bg-[#2A2A2A] dark:text-[#F1F1F1] dark:hover:bg-[#3A3A3A] border border-gray-100 dark:border-[#C0A172]/20'}`}
                    >
                      {t(category)}
                    </button>
                  ))}
                </motion.div>

                {/* Services Grid */}
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                  {filteredItems.map((item: any) => (
                    <motion.div
                      key={item.id}
                      variants={itemVariants}
                      whileHover={{ y: -8, boxShadow: '0 25px 50px -12px rgba(192, 161, 114, 0.15)' }}
                      transition={{ duration: 0.3 }}
                      onClick={() => onNavigate('booking', { service: item.id })}
                      className="bg-white dark:bg-[#2A2A2A] rounded-3xl shadow-lg overflow-hidden border border-gray-100 dark:border-[#C0A172]/10 flex flex-col cursor-pointer transition-all duration-300"
                    >
                      <img src={getImageUrl(item.imageUrl)} alt={t(item.titleKey)} className="w-full h-56 object-cover" />
                      <div className="p-8 flex flex-col flex-grow">
                        <div className="flex justify-between items-start mb-2">
                           <h3 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1]">{t(item.titleKey)}</h3>
                           {item.isOnlineService && (
                             <span className="bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200 text-xs font-bold px-2 py-1 rounded-full whitespace-nowrap">
                               {t('portfolioPage_onlineService') || 'Online Service'}
                             </span>
                           )}
                        </div>
                        <p className="text-gray-600 dark:text-[#F1F1F1]/70 mb-8 flex-grow leading-relaxed">{t(item.descriptionKey)}</p>

                        <div className="grid grid-cols-1 gap-4 text-sm mt-auto pt-8 border-t border-gray-50 dark:border-[#C0A172]/10">
                          {item.pricingTiers.map((tier: any) => (
                            <div key={tier.id} className={`p-6 rounded-2xl ${tier.isFeatured ? 'bg-gray-50 dark:bg-[#18181B] border-2 border-[#C0A172] shadow-sm' : 'bg-gray-50 dark:bg-[#18181B] border border-gray-100 dark:border-[#C0A172]/10'}`}>
                              <h4 className="font-black text-gray-900 dark:text-[#F1F1F1] mb-2 flex items-center gap-2 uppercase tracking-widest text-[10px]">
                                {tier.isFeatured && <SparklesIcon className="w-4 h-4 text-[#C0A172]" />}
                                {t(tier.nameKey)}
                              </h4>
                              <div className="font-serif font-bold text-[#C0A172] text-xl mb-3">
                                <PriceDisplay
                                  basePriceEUR={tier.basePriceEUR}
                                  serviceId={item.id}
                                  fallbackKey={tier.priceKey}
                                />
                              </div>
                              <ul className="space-y-2 text-gray-600 dark:text-[#F1F1F1]/60">
                                {tier.featuresKey.map((feature: any) => <li key={feature} className="flex gap-2">
                                  <span className="text-[#C0A172] font-black">•</span>
                                  {t(feature)}
                                </li>)}
                              </ul>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={() => onNavigate('booking', { service: item.id })}
                          className="w-full mt-10 bg-[#C0A172] hover:bg-[#A68A5E] text-white font-bold py-4 px-6 rounded-full transition-all duration-300 transform active:scale-95 text-base shadow-xl"
                        >
                          {t('portfolioPage_bookService')}
                        </button>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>
      </div>
      {/* Membership Section */}
      {UIX_CONFIG.ENABLE_MEMBERSHIPS && (
        <motion.section variants={sectionVariants} className="mb-16">
          <div className="flex items-center justify-between mb-8 border-b border-gray-100 dark:border-white/5 pb-4">
            <div className="text-left">
              <h2 className="text-3xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1]">{t('portfolioPage_membership_title')}</h2>
              <p className="text-gray-600 dark:text-[#F1F1F1]/60 mt-2 max-w-3xl mx-auto">{t('portfolioPage_membership_tagline')}</p>
            </div>
            <button
              onClick={() => setIsMembershipsCollapsed(!isMembershipsCollapsed)}
              className="p-3 rounded-full hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              aria-label={isMembershipsCollapsed ? "Expand memberships" : "Collapse memberships"}
            >
              <ChevronDownIcon className={`w-6 h-6 transition-transform duration-300 ${isMembershipsCollapsed ? '-rotate-90' : 'rotate-0'}`} />
            </button>
          </div>

          <AnimatePresence initial={false}>
            {!isMembershipsCollapsed && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
                className="overflow-hidden"
              >
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }} className="flex justify-center items-center gap-2 mb-10 p-1 bg-gray-200 dark:bg-gray-700/50 rounded-full w-fit mx-auto">
                  <button onClick={() => setBillingCycle('weekly')} className={`${billingCycleButtonClass} ${billingCycle === 'weekly' ? activeBillingCycleButtonClass : inactiveBillingCycleButtonClass}`}>{t('membership_cycle_weekly')}</button>
                  <button onClick={() => setBillingCycle('monthly')} className={`${billingCycleButtonClass} ${billingCycle === 'monthly' ? activeBillingCycleButtonClass : inactiveBillingCycleButtonClass}`}>{t('membership_cycle_monthly')}</button>
                  <button onClick={() => setBillingCycle('quarterly')} className={`${billingCycleButtonClass} ${billingCycle === 'quarterly' ? activeBillingCycleButtonClass : inactiveBillingCycleButtonClass}`}>{t('membership_cycle_quarterly')}</button>
                </motion.div>
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-stretch">
                  {membershipTiers.map(tier => {
                    const priceInfo = tier.prices.find(p => p.cycle === billingCycle);
                    return (
                      <motion.div
                        key={tier.id}
                        variants={itemVariants}
                        className={`bg-white dark:bg-[#2A2A2A] rounded-3xl shadow-lg border flex flex-col p-8 transition-transform hover:-translate-y-1 ${tier.isFeatured ? 'border-4 border-[#C0A172] relative' : 'border-gray-100 dark:border-[#C0A172]/10'}`}
                      >
                        {tier.isFeatured && <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#C0A172] text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest shadow-lg">{t('membership_most_popular')}</span>}
                        <h3 className="text-2xl font-serif font-bold text-gray-900 dark:text-[#F1F1F1] text-center">{t(tier.nameKey)}</h3>
                        <div className="mt-4 text-center relative h-16 flex items-center justify-center">
                          <span className="text-5xl font-bold text-gray-900 dark:text-[#F1F1F1] font-serif">
                            {tier.id === 'free' ? (
                              <span className="text-5xl font-bold text-gray-900 dark:text-[#F1F1F1] font-serif">
                                {t('membership_cycle_free')}
                              </span>
                            ) : (
                              <PriceDisplay
                                basePriceEUR={priceInfo?.basePriceEUR || 0}
                                tierId={tier.id}
                                fallbackKey={priceInfo?.priceKey}
                                className="items-center"
                              />
                            )}
                          </span>
                          {priceInfo?.discountKey && <span className="absolute -top-4 -right-2 bg-amber-400 text-black text-xs font-black px-3 py-1.5 rounded-full transform rotate-12 shadow-md">{t(priceInfo.discountKey)}</span>}
                        </div>
                        <ul className="mt-8 space-y-3 text-gray-600 dark:text-gray-300 flex-grow">
                          {tier.featuresKey.map(feature => (
                            <li key={feature} className="flex items-start">
                              <span className="text-green-500 mr-2 mt-1">&#10003;</span>
                              <span>{t(feature)}</span>
                            </li>
                          ))}
                        </ul>
                        <button
                          onClick={() => onNavigate('profile', { intent: 'subscribe', tier: tier.id })}
                          className={`w-full mt-10 font-bold py-4 px-6 rounded-full transition-all duration-300 transform active:scale-95 text-base shadow-xl ${tier.isFeatured ? 'bg-[#C0A172] text-white hover:bg-[#A68A5E]' : 'bg-gray-900 dark:bg-[#18181B] text-white hover:bg-gray-800 dark:hover:bg-black'}`}
                        >
                          {t('membership_subscribe_button')}
                        </button>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.section>
      )}
      <SchemaMarkup
        type="Service"
        data={useMemo(() => ({
          name: "Constantina AI Stylist Services",
          provider: {
            "@type": "Organization",
            name: "Constantina",
            url: "https://constantinalifestyle.one"
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: "Personal Styling Services",
            itemListElement: portfolioItems.map((item: any, index: number) => ({
              "@type": "ListItem",
              position: index + 1,
              item: {
                "@type": "Service",
                name: t(item.titleKey),
                description: t(item.descriptionKey),
                image: getImageUrl(item.imageUrl),
                offers: item.pricingTiers.map((tier: any) => ({
                  "@type": "Offer",
                  name: t(tier.nameKey),
                  price: tier.basePriceEUR,
                  priceCurrency: "EUR",
                  availability: "https://schema.org/InStock"
                }))
              }
            }))
          }
        }), [portfolioItems, t])}
      />
      <Footer onNavigate={onNavigate} />
      <StickyCTA
        show={showSticky}
        label={t('portfolioPage_bookService')}
        onClick={() => onNavigate('booking')}
        icon={<SparklesIcon className="w-5 h-5" />}
      />
    </motion.div >
  );
};

export default ServicesPage;
